import {dist, moveTowardsAngle} from "../engine/utils/math";
import {Position} from "../components/transform/position";
import {MoveGrabState} from "../components/grab/moveGrabState";
import {PumpPressure} from "../components/inputObject/pumpPressure";
import {Rotation} from "../components/transform/rotation";
import {Vector2} from "../engine/utils/Vector2";
import {Button, ButtonEvent} from "../components/inputObject/button";
import {RotationGrabState} from "../components/grab/rotationGrabState";
import {AppState} from "../app";
import {RotationMotor} from "../components/inputObject/rotationMotor";
import {Pointer} from "../engine/tools/InputManager";
import {settings} from "../settings";

export interface GrabPointer extends Pointer {

    grabbedObject: any
    overlapObjects: any[]
}

export function updateInput({interactiveCanvas, entities, time,startupTimestamp}: AppState) {

    const {ctx, camera, input} = interactiveCanvas

    if(settings.loop !== undefined)
    {
        const autoReloadIfNoInputWaitTime = 60
        const maxTimestamp = interactiveCanvas.input.pointers.map(p=>p.timestamp).reduce((a,b)=>Math.max(a,b),startupTimestamp)
        const timeSinceLastInput = (Date.now()-maxTimestamp)/1000
        if(timeSinceLastInput > autoReloadIfNoInputWaitTime)
            window.location.reload();
    }

    // ROTATE GRAB
    entities.forEach([Rotation, RotationGrabState], (e) => {

            if (e.RotationGrabState.pointer) {

                if (e.RotationGrabState.pointer.isPressed) {

                    const worldPos = camera.cameraToWorld(e.RotationGrabState.pointer.position)
                    const inputAngle = worldPos.subtract(e.Position.value).getDirection()
                    const inputAngleOffset = inputAngle - e.RotationGrabState.startInputAngle
                    const targetAngle = e.RotationGrabState.startObjectAngle + inputAngleOffset;

                    e.Rotation.value = moveTowardsAngle(e.Rotation.value, targetAngle, time.deltaTime * 30)
                } else {

                    delete e.RotationGrabState.pointer.grabbedObject
                    delete e.RotationGrabState.pointer

                }

            } else {

                for (const pointer of input.pointers) {
                    if (!pointer.isPressed)
                        return

                    const grabPointer = pointer as GrabPointer
                    if (grabPointer.grabbedObject)
                        continue

                    const worldPos = camera.cameraToWorld(pointer.position)
                    const grabHandlePosition = e.GrabAnchor ? e.GrabAnchor.target.Position.value : e.Position.value
                    const distToHandle = worldPos.subtract(grabHandlePosition).getMagnitude()

                    const grabRadius = 80
                    if (distToHandle < grabRadius) {
                        grabPointer.grabbedObject = e
                        e.RotationGrabState.pointer = pointer
                        e.RotationGrabState.startObjectAngle = e.Rotation.value
                        e.RotationGrabState.startInputAngle = worldPos.subtract(e.Position.value).getDirection()
                    }
                }
            }
        }
    )

    for(const pointer of input.pointers)
    {
        (pointer as GrabPointer).overlapObjects = []
    }
    entities.forEach([Position, MoveGrabState], (e) => {

        for (const pointer of input.pointers) {

            const grabPointer = pointer as GrabPointer
            const worldPos = camera.cameraToWorld(pointer.position)
            const grabOffset = worldPos.subtract(e.Position.value)
            const distToHandle = grabOffset.getMagnitude()

            if (distToHandle < e.MoveGrabState.grabRadius) {
                grabPointer.overlapObjects.push(e)
            }
        }
    })

    // MOVE GRAB
    entities.forEach([Position, MoveGrabState], (e) => {

            if (e.MoveGrabState.pointer) {

                if (e.MoveGrabState.pointer.isPressed) {

                    const worldPos = camera.cameraToWorld(e.MoveGrabState.pointer.position)
                    const pos = worldPos.subtract(e.MoveGrabState.grabOffset);

                    const clampedPos = e.MoveGrabState.rect.clamp(pos)
                    const lastPosition = e.Position.value
                    e.Position.value = Vector2.moveTowards(e.Position.value, clampedPos, e.MoveGrabState.maxSpeed * time.deltaTime)
                    e.MoveGrabState.speed.set(e.Position.value.subtract(lastPosition))
                } else {

                    delete e.MoveGrabState.pointer.grabbedObject
                    delete e.MoveGrabState.pointer
                    e.MoveGrabState.speed.set(0, 0)

                }

            } else {

                for (const pointer of input.pointers) {
                    if (!pointer.isPressed)
                        return

                    const grabPointer = pointer as GrabPointer
                    if (grabPointer.grabbedObject)
                        continue

                    if (grabPointer.overlapObjects.includes(e)){

                        const worldPos = camera.cameraToWorld(pointer.position)
                        const grabOffset = worldPos.subtract(e.Position.value)

                        grabPointer.grabbedObject = e
                        e.MoveGrabState.pointer = pointer
                        e.MoveGrabState.grabOffset = grabOffset
                        e.MoveGrabState.lastPosition = e.Position.value
                    }
                }
            }
        }
    )

    // PUMP
    entities.forEach([PumpPressure, MoveGrabState], (e) => {

        e.MoveGrabState.maxSpeed = -e.MoveGrabState.speed.y > 0 ? 600 : 2000
    })
    entities.forEach([PumpPressure], (e) => {

        e.PumpPressure.lastPressure = e.PumpPressure.pressure;


        if (e.MoveGrabState.speed.y < 0) {
            e.PumpPressure.timeSinceLastPump = 0;
        } else {
            e.PumpPressure.timeSinceLastPump += time.deltaTime;
        }

        const start = 0.2
        if (e.PumpPressure.timeSinceLastPump > start  )
            e.PumpPressure.pressure += -Math.max((e.PumpPressure.timeSinceLastPump - start) * 3, 2)*0.02 * time.deltaTime

        e.PumpPressure.pressure += Math.max(-e.MoveGrabState.speed.y, 0) * time.deltaTime * e.PumpSpeed.speed
        e.PumpPressure.pressure = Math.max(0, e.PumpPressure.pressure)

    })


    // BUTTON
    entities.forEach([Button, Position], (e) => {

        const isOverlapping = (worldPos) => dist(worldPos.x, worldPos.y, e.Position.value.x, e.Position.value.y) < 100

        e.Button.event = ButtonEvent.None;

        if (!e.Button.pointer) {

            for (const pointer of input.pointers) {
                if (!pointer.isPressed)
                    continue

                const pos = camera.cameraToWorld(pointer.position)
                if (isOverlapping(pos)) {
                    e.Button.pointer = pointer
                    e.Button.event = ButtonEvent.Down;
                    break
                }
            }
        } else {
            const pos = camera.cameraToWorld(e.Button.pointer.position)
            const lastIsDown = e.Button.isDown
            e.Button.isDown = isOverlapping(pos)

            if (!e.Button.pointer.isPressed) {
                e.Button.pointer = undefined

                if (e.Button.isDown) {
                    e.Button.isDown = false
                    e.Button.event = ButtonEvent.UpClicked;
                } else {
                    e.Button.event = ButtonEvent.UpCanceled;
                }
            } else {

                if (e.Button.isDown != lastIsDown) {
                    if (e.Button.isDown)
                        e.Button.event = ButtonEvent.DownWhileHolding;
                    else
                        e.Button.event = ButtonEvent.UpWhileHolding;
                }
            }
        }
    })

    entities.forEach([ Rotation, RotationGrabState, RotationMotor], (e) => {

        e.RotationMotor.active = !e.RotationGrabState.pointer
    })
}